<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import type { TScheduleStatus } from '@/domain/catalog/availability';
  import { statusText } from '@/domain/catalog/catalog-status-text';
  import { ICatalogRowView, thumbPath } from '@/domain/catalog/catalog-view';
  import { Globals } from '@/modules/Globals';
  import V2CatalogPriceField from './V2CatalogPriceField.vue';

  /**
   * One product of the list (plan §5.8). It renders a frozen view object and
   * owns no watchers, observers or popovers (1,262 rows on old hardware):
   * the page's single panel host opens the chooser, price and ⋯ panels
   * anchored to the element each event carries. Icons are plain `mdi` tags.
   *
   * Wide and compact rows share one DOM (a grid places the parts), so a
   * width change never destroys a focused control or a panel's anchor.
   */
  const props = withDefaults(
    defineProps<{
      view: ICatalogRowView;
      /** The time-dependent status (recomputed by the page every minute). */
      schedule?: TScheduleStatus | null;
      branchName: string;
      wide: boolean;
      selecting?: boolean;
      selected?: boolean;
      /** The assistant points at it («Ver en el menú»), for a moment. */
      highlighted?: boolean;
      /** Open in the editor. */
      editing?: boolean;
      /** The category is hidden: the row dims. */
      dimmed?: boolean;
      canManage: boolean;
      canToggle: boolean;
      online: boolean;
      /** Why the switch is disabled for this role, if it is. */
      toggleBlockedTitle?: string | null;
      photoBusy?: boolean;
    }>(),
    {
      schedule: null,
      selecting: false,
      selected: false,
      highlighted: false,
      editing: false,
      dimmed: false,
      toggleBlockedTitle: null,
      photoBusy: false,
    }
  );

  const emit = defineEmits<{
    (e: 'open'): void;
    (e: 'toggle', payload: { on: boolean; anchor: HTMLElement | null }): void;
    (e: 'price', anchor: HTMLElement): void;
    (e: 'photo'): void;
    (e: 'menu', anchor: HTMLElement): void;
    (e: 'select'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const switchAnchor = ref<HTMLElement | null>(null);
  const thumbFailed = ref(false);

  const photoUrl = computed(() => {
    const picture = props.view.picture;
    if (!picture) return null;
    return `${Globals.uploadsPath}/${
      thumbFailed.value ? picture : thumbPath(picture)
    }`;
  });

  const status = computed(() =>
    statusText(t, props.view.status, props.schedule, props.branchName)
  );

  const sizesChip = computed(() =>
    props.view.sizesCount > 1
      ? String(
          proxy.$tc('menuPage.row.sizes', props.view.sizesCount, {
            n: props.view.sizesCount,
          })
        )
      : null
  );

  const groupsChip = computed(() =>
    props.view.groupsCount
      ? String(
          proxy.$tc('menuPage.row.groups', props.view.groupsCount, {
            n: props.view.groupsCount,
          })
        )
      : null
  );

  const writeTitle = computed(() =>
    props.online ? undefined : t('menuPage.errors.offline')
  );

  function onToggle(on: boolean) {
    emit('toggle', { on, anchor: switchAnchor.value });
  }

  function onPrice(event: MouseEvent) {
    if (event.currentTarget instanceof HTMLElement)
      emit('price', event.currentTarget);
  }

  function onMenu(event: MouseEvent) {
    if (event.currentTarget instanceof HTMLElement)
      emit('menu', event.currentTarget);
  }

  /** A click on the row's empty space opens it too; controls stop propagation. */
  function onRowClick() {
    if (props.selecting) emit('select');
    else emit('open');
  }
</script>

<template>
  <div
    class="v2-catalog-product-row"
    :class="{
      'is-wide': wide,
      'is-editing': editing,
      'is-unavailable': !view.available,
      'is-dimmed': dimmed,
      'is-selected': selecting && selected,
      'is-selecting': selecting,
      'is-highlighted': highlighted,
    }"
    :data-item-id="view.id"
    @click="onRowClick"
  >
    <label v-if="selecting" class="row-check" @click.stop>
      <input
        type="checkbox"
        class="check-input"
        :checked="selected"
        :aria-label="t('menuPage.row.select', { name: view.name })"
        @change="emit('select')"
      />
    </label>

    <button
      v-if="canManage"
      type="button"
      class="row-photo"
      :class="{ 'has-photo': !!photoUrl }"
      :disabled="!online || photoBusy"
      :title="writeTitle"
      :aria-label="
        t(photoUrl ? 'menuPage.row.changePhoto' : 'menuPage.row.addPhoto', {
          name: view.name,
        })
      "
      @click.stop="emit('photo')"
    >
      <img
        v-if="photoUrl"
        :src="photoUrl"
        alt=""
        class="photo-image"
        loading="lazy"
        @error="thumbFailed = true"
      />
      <i v-else class="mdi mdi-camera-plus-outline photo-empty" />
      <span v-if="photoBusy" class="photo-busy"
        ><span class="busy-spinner"
      /></span>
    </button>
    <!-- Roles that cannot edit see the photo, never an invitation to add one. -->
    <span
      v-else
      class="row-photo is-static"
      :class="{ 'has-photo': !!photoUrl }"
    >
      <img
        v-if="photoUrl"
        :src="photoUrl"
        alt=""
        class="photo-image"
        loading="lazy"
        @error="thumbFailed = true"
      />
      <i v-else class="mdi mdi-silverware-fork-knife photo-none" />
    </span>

    <div class="row-body">
      <button type="button" class="row-open" @click.stop="onRowClick">
        <span class="row-name" :title="view.name">{{ view.name }}</span>
        <span v-if="view.description" class="row-description">{{
          view.description
        }}</span>
      </button>
      <div v-if="wide && (sizesChip || groupsChip)" class="row-meta">
        <span v-if="sizesChip" class="row-chip">{{ sizesChip }}</span>
        <span v-if="groupsChip" class="row-chip">{{ groupsChip }}</span>
      </div>
    </div>

    <div class="row-price">
      <V2CatalogPriceField
        :price="view.price"
        :name="view.name"
        :disabled="!canManage || !online"
        :disabled-title="writeTitle"
        @click="onPrice"
      />
      <span v-if="!wide && sizesChip" class="row-chip">{{ sizesChip }}</span>
    </div>

    <span v-if="status" class="row-status" :class="`tone-${status.tone}`">
      <span class="status-dot" :class="{ 'is-hollow': status.hollow }" />
      {{ status.text }}
    </span>

    <span
      ref="switchAnchor"
      class="row-switch"
      :title="toggleBlockedTitle || writeTitle"
      @click.stop
    >
      <PosUiSwitch
        :value="view.available"
        :disabled="!canToggle || !online || !!toggleBlockedTitle"
        :aria-label="t('menuPage.row.available', { name: view.name })"
        @input="onToggle"
      />
    </span>

    <!-- Hidden (not removed) on compact rows: its actions live in the editor. -->
    <button
      type="button"
      class="row-menu"
      :aria-label="t('menuPage.row.actions', { name: view.name })"
      @click.stop="onMenu"
    >
      <i class="mdi mdi-dots-horizontal" />
    </button>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-product-row {
    // Compact: photo | name, price, status | switch. Wide adds the price
    // and ⋯ columns. Margins, not grid gaps, space the parts.
    @apply tw-relative tw-grid tw-cursor-pointer tw-items-start tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-py-3 tw-pl-3 tw-pr-2 tw-transition-colors tw-duration-150;
    grid-template-columns: auto auto minmax(0, 1fr) auto;
    grid-template-areas:
      'check photo body switch'
      'check photo price switch'
      'check photo status switch';

    &:hover {
      @apply tw-bg-neutral-50;
    }

    &.is-wide {
      grid-template-columns: auto auto minmax(0, 1fr) auto auto auto;
      grid-template-areas:
        'check photo body price switch menu'
        'check photo status price switch menu';

      .row-price {
        @apply tw-ml-3 tw-mt-0 tw-self-center tw-justify-end;
      }

      .row-switch,
      .row-menu {
        @apply tw-self-center;
      }

      .row-menu {
        @apply tw-flex;
      }
    }

    // Checked in selection mode (the checkbox carries the state).
    &.is-selected {
      @apply tw-bg-neutral-50;
    }

    // Open in the editor: the one orange «you are here» of the list.
    &.is-editing {
      @apply tw-bg-orange-50;
      box-shadow: inset 3px 0 0 rgb(var(--c-orange-600));
    }

    // Pointed at by the assistant («Ver en el menú»): a brief flash.
    &.is-highlighted {
      @apply tw-bg-orange-50 tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      transition: background-color 400ms ease;

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }

    .row-check {
      @apply tw-mr-1 tw-flex tw-h-12 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center;
      grid-area: check;

      .check-input {
        @apply tw-h-5 tw-w-5 tw-cursor-pointer;
        accent-color: rgb(var(--c-neutral-900));
      }
    }

    .row-photo {
      @apply tw-relative tw-mr-3 tw-flex tw-h-12 tw-w-12 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border tw-border-dashed tw-border-neutral-300 tw-bg-neutral-50 tw-p-0 tw-text-neutral-400 tw-transition-colors;
      grid-area: photo;

      &.has-photo {
        @apply tw-border-solid tw-border-neutral-200 tw-bg-surface;
      }

      &.is-static {
        @apply tw-cursor-default tw-border-solid tw-border-neutral-200;
      }

      &:hover:not(:disabled):not(.is-static) {
        @apply tw-border-neutral-400 tw-text-neutral-600;
      }

      &:disabled {
        @apply tw-cursor-default;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .photo-image {
        @apply tw-h-full tw-w-full tw-object-cover;
      }

      .photo-empty {
        @apply tw-text-2xl;
      }

      .photo-none {
        @apply tw-text-xl tw-text-neutral-300;
      }

      .photo-busy {
        @apply tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-flex tw-items-center tw-justify-center tw-bg-surface/80;
      }

      .busy-spinner {
        @apply tw-h-5 tw-w-5 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-400;
        border-top-color: transparent;
      }
    }

    .row-body {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-items-start;
      grid-area: body;
    }

    .row-open {
      @apply tw-flex tw-w-full tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-start tw-rounded tw-border-0 tw-bg-transparent tw-p-0 tw-text-left;

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .row-name {
      @apply tw-max-w-full tw-overflow-hidden tw-font-semibold tw-text-neutral-900;
      font-size: 15px;
      line-height: 20px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .row-description {
      @apply tw-mt-0.5 tw-max-w-full tw-truncate tw-text-sm tw-text-neutral-500;
    }

    .row-meta {
      @apply tw-mt-1.5 tw-flex tw-flex-wrap;

      .row-chip + .row-chip {
        @apply tw-ml-1.5;
      }
    }

    .row-price {
      @apply tw-mt-2 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center;
      grid-area: price;

      .row-chip {
        @apply tw-ml-2;
      }
    }

    .row-chip {
      @apply tw-inline-flex tw-h-6 tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border tw-border-solid tw-border-neutral-200 tw-px-2 tw-text-xs tw-text-neutral-600;
    }

    // Never dimmed: it says why the row is unavailable.
    .row-status {
      @apply tw-mt-1.5 tw-inline-flex tw-items-center tw-self-start tw-text-xs tw-font-medium;
      grid-area: status;

      &.tone-neutral {
        @apply tw-text-neutral-600;
      }

      &.tone-amber {
        @apply tw-text-amber-800;

        .status-dot {
          @apply tw-bg-amber-500;
        }
      }

      .status-dot {
        @apply tw-mr-1.5 tw-h-[7px] tw-w-[7px] tw-flex-none tw-rounded-full tw-bg-neutral-400;

        &.is-hollow {
          @apply tw-border-[1.5px] tw-border-solid tw-border-neutral-400 tw-bg-transparent;
        }
      }
    }

    .row-switch {
      @apply tw-ml-3 tw-flex tw-flex-none tw-cursor-default;
      grid-area: switch;
    }

    .row-menu {
      @apply tw-ml-1 tw-hidden tw-h-11 tw-w-11 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-xl tw-text-neutral-500;
      grid-area: menu;

      &:hover {
        @apply tw-bg-neutral-100 tw-text-neutral-800;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    // Unavailable: the photo, texts and price dim; the status and switch do not.
    &.is-unavailable,
    &.is-dimmed {
      .row-photo,
      .row-open,
      .row-meta,
      .row-price {
        opacity: 0.55;
      }
    }
  }
</style>
